import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const SERVICE_LABELS = {
  financial_analyst_support: "Financial Analyst Support",
  masterclass: "Masterclass",
  training_cohort: "Training Cohort",
  monthly_webinar: "Monthly Webinar",
};
const STATUS_TXT = {
  active: "Active", paused: "Paused", non_responsive: "Non-Responsive",
  not_ready: "Not Ready to Raise", other: "Other",
};
const fmt = (iso) => (iso ? iso.slice(0, 10) : "—");

function Section({ title, count, children }) {
  return (
    <div className="mt-3">
      <p className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
        {title} <span className="text-[#2dd4bf]">({count})</span>
      </p>
      <div className="mt-1 max-h-48 space-y-1 overflow-y-auto pr-1">
        {count === 0 ? <p className="text-[11px] text-neutral-600">None this month.</p> : children}
      </div>
    </div>
  );
}

export const RMActivityReport = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!month) return;
    api.get("/admin/rms/activity-report", { params: { month } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Couldn't load the activity report"));
  }, [month]);

  return (
    <div data-testid="rm-activity-report">
      <div className="flex items-center gap-3">
        <label className="text-xs font-semibold text-neutral-400">Report month</label>
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
          className="rounded-md border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm text-neutral-100 outline-none focus:border-[#0A8B85]"
          data-testid="rm-report-month" />
      </div>

      {!data ? (
        <p className="mt-6 text-sm text-neutral-500">Loading…</p>
      ) : (
        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {data.rms.map((r) => (
            <div key={r.user_id} className="rounded-2xl border border-neutral-800 bg-neutral-950 p-4" data-testid={`rm-report-card-${r.user_id}`}>
              <p className="font-bold text-neutral-100">{r.name}</p>
              <p className="text-[11px] text-neutral-500">{r.email}</p>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-neutral-900 py-2">
                  <p className="text-lg font-extrabold text-[#2dd4bf]" data-testid={`rm-report-checkins-${r.user_id}`}>{r.checkins.length}</p>
                  <p className="text-[10px] uppercase tracking-wide text-neutral-500">Check-ins</p>
                </div>
                <div className="rounded-lg bg-neutral-900 py-2">
                  <p className="text-lg font-extrabold text-amber-300" data-testid={`rm-report-changes-${r.user_id}`}>{r.status_changes.length}</p>
                  <p className="text-[10px] uppercase tracking-wide text-neutral-500">Status changes</p>
                </div>
                <div className="rounded-lg bg-neutral-900 py-2">
                  <p className="text-lg font-extrabold text-sky-300" data-testid={`rm-report-services-${r.user_id}`}>{r.services.length}</p>
                  <p className="text-[10px] uppercase tracking-wide text-neutral-500">Services intro'd</p>
                </div>
              </div>

              <Section title="Check-ins" count={r.checkins.length}>
                {r.checkins.map((c) => (
                  <p key={c.checkin_id} className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300">
                    <span className="font-semibold text-neutral-400">{c.checkin_date || fmt(c.created_at)}</span> · {c.company_name}
                    {c.note && <span className="text-neutral-500"> — {c.note.slice(0, 80)}{c.note.length > 80 ? "…" : ""}</span>}
                  </p>
                ))}
              </Section>

              <Section title="Status changes" count={r.status_changes.length}>
                {r.status_changes.map((c) => (
                  <p key={c.change_id} className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300">
                    <span className="font-semibold text-neutral-400">{fmt(c.changed_at)}</span> · {c.company_name} — {STATUS_TXT[c.from_status] || "None"} → <b className="text-amber-300">{STATUS_TXT[c.to_status] || c.to_status}</b>
                    {c.reason && <span className="italic text-neutral-500"> ({c.reason})</span>}
                  </p>
                ))}
              </Section>

              <Section title="Services introduced" count={r.services.length}>
                {r.services.map((s, i) => (
                  <p key={`${s.company_id}-${s.service}-${i}`} className="rounded-md bg-neutral-900 px-2 py-1 text-[11px] text-neutral-300">
                    <span className="font-semibold text-neutral-400">{s.date}</span> · {s.company_name} — <b className="text-sky-300">{SERVICE_LABELS[s.service] || s.service}</b>
                  </p>
                ))}
              </Section>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
